// src/pages/CategoriesPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import products from '../data/products';
import './CategoriesPage.css';

const CategoriesPage = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Ref for filter bar
  const filterBarRef = useRef(null);

  const categories = [...new Set(products.map(p => p.category))];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Handle filter bar sticky effect
  useEffect(() => {
    const handleScroll = () => {
      if (filterBarRef.current) {
        const filterBarTop = filterBarRef.current.offsetTop;
        setIsFilterSticky(window.scrollY > filterBarTop - 80);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Filter and sort categories
  const filteredCategories = useCallback(() => {
    let filtered = categories.filter(category => {
      const categoryName = category.replace('-', ' ').toLowerCase();
      return categoryName.includes(searchTerm.toLowerCase());
    });

    // Apply sorting
    switch(sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.localeCompare(b));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.localeCompare(a));
        break;
      case 'popularity':
        filtered.sort((a, b) => {
          const countA = products.filter(p => p.category === a).length;
          const countB = products.filter(p => p.category === b).length;
          return countB - countA;
        });
        break;
      default:
        // Default sorting (by name)
        filtered.sort((a, b) => a.localeCompare(b));
    }

    return filtered;
  }, [categories, searchTerm, sortBy]);

  const displayedCategories = filteredCategories();

  // Scroll to filters
  const scrollToFilters = useCallback(() => {
    if (filterBarRef.current) {
      const yOffset = -20;
      const y = filterBarRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  }, []);

  // Handle category click
  const handleCategoryClick = (category) => {
    navigate(`/category/${category}`);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle home click
  const handleHomeClick = (e) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle contact click
  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/contact');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getCategoryStats = (category) => {
    const categoryProducts = products.filter(p => p.category === category);
    const strains = [...new Set(categoryProducts.map(p => p.strain))];
    const minPrice = Math.min(...categoryProducts.map(p => 
      p.priceOptions ? Math.min(...p.priceOptions.map(opt => opt.price)) : p.price
    ));
    const maxPrice = Math.max(...categoryProducts.map(p => 
      p.priceOptions ? Math.max(...p.priceOptions.map(opt => opt.price)) : p.price
    ));

    return {
      count: categoryProducts.length,
      strains: strains.length,
      priceRange: minPrice !== maxPrice ? `$${minPrice} - $${maxPrice}` : `$${minPrice}`
    };
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Flower':
      case 'Exotic-Flower':
        return '🌿';
      case 'Edibles':
        return '🍬';
      case 'Concentrates':
        return '⚗️';
      case 'cart':
        return '🔋';
      case 'disposable-cart':
        return '💨';
      case 'Pre-rolls':
        return '🚬';
      case 'Shake':
        return '🍃';
      default:
        return '📦';
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Flower':
        return 'var(--color-flower)';
      case 'Exotic-Flower':
        return 'var(--color-exotic)';
      case 'Edibles':
        return 'var(--color-edibles)';
      case 'Concentrates':
        return 'var(--color-concentrates)';
      case 'cart':
        return 'var(--color-cart)';
      case 'disposable-cart':
        return 'var(--color-disposable)';
      case 'Pre-rolls':
        return 'var(--color-prerolls)';
      case 'Shake':
        return 'var(--color-shake)';
      default:
        return 'var(--color-default)';
    }
  };

  return (
    <div className="categories-page">
      {/* Filter Bar */}
      <div 
        ref={filterBarRef}
        className={`filter-bar ${isFilterSticky ? 'sticky' : ''}`}
      >
        <div className="container">
          <div className="filter-bar-content">
            <div className="filter-bar-left">
              <button 
                className="filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
                aria-expanded={showFilters}
              >
                <svg className="filter-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M3 4h18M3 12h18M3 20h18M8 8v8M16 8v8" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Search</span>
                {searchTerm && (
                  <span className="filter-badge">1</span>
                )}
              </button>

              <div className="view-options">
                <button 
                  className={`view-option ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                    <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
                <button 
                  className={`view-option ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="10" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                    <rect x="3" y="17" width="18" height="4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="filter-bar-right">
              <select 
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort by"
              >
                <option value="default">Default</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
                <option value="popularity">Most Popular</option>
              </select>

              <p className="results-count">
                <span className="count-number">{displayedCategories.length}</span>
                <span className="count-label">categories</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="categories-section">
        <div className="container">
          {/* Header */}
          <div className="categories-header">
            <div className="breadcrumb">
              <button 
                onClick={handleHomeClick}
                className="breadcrumb-link"
              >
                Home
              </button>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">Categories</span>
            </div>
            
            <div className="header-main">
              <div className="header-title-wrapper">
                <h1 className="categories-title">Product Categories</h1>
                <p className="categories-subtitle">
                  Explore our curated collection of premium products
                </p>
              </div>
              
              <div className="header-stats">
                <div className="stat-box">
                  <div className="stat-number">{categories.length}</div>
                  <div className="stat-label">Categories</div>
                </div>
                <div className="stat-box">
                  <div className="stat-number">{products.length}</div>
                  <div className="stat-label">Total Products</div>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar (Mobile) */}
          <div className="search-section">
            <div className="search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Active Filters */}
          {searchTerm && (
            <div className="active-filters">
              <span className="active-filter-label">Active Filter:</span>
              <button 
                className="active-filter-chip"
                onClick={() => setSearchTerm('')}
              >
                Search: "{searchTerm}"
                <span className="remove-filter">✕</span>
              </button>
            </div>
          )}

          {/* Categories Grid/List */}
          {displayedCategories.length > 0 ? (
            <div className={`categories-container ${viewMode}`}>
              {displayedCategories.map((category, index) => {
                const stats = getCategoryStats(category);
                const icon = getCategoryIcon(category);
                const color = getCategoryColor(category);
                
                return (
                  <button
                    key={category}
                    onClick={() => handleCategoryClick(category)}
                    className={`category-card ${viewMode}`}
                    style={{ '--card-color': color }}
                    data-aos="fade-up"
                    data-aos-delay={index * 50}
                  >
                    <div className="card-corner">
                      <div className="corner-decoration"></div>
                    </div>
                    
                    <div className="card-header">
                      <div className="category-icon-wrapper">
                        <div className="icon-backdrop"></div>
                        <span className="category-icon">{icon}</span>
                      </div>
                      <span className="arrow-indicator">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </span>
                    </div>
                    
                    <div className="card-content">
                      <h3 className="category-name">
                        {category.replace('-', ' ')}
                      </h3>
                      
                      <div className="category-stats">
                        <div className="stat-item">
                          <div className="stat-icon-wrapper">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                            </svg>
                          </div>
                          <div className="stat-text">
                            <div className="stat-value">{stats.count}</div>
                            <div className="stat-label">Products</div>
                          </div>
                        </div>
                        
                        <div className="stat-item">
                          <div className="stat-icon-wrapper">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                          </div>
                          <div className="stat-text">
                            <div className="stat-value">{stats.strains}</div>
                            <div className="stat-label">Strains</div>
                          </div>
                        </div>
                        
                        <div className="stat-item">
                          <div className="stat-icon-wrapper">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                          </div>
                          <div className="stat-text">
                            <div className="stat-value">{stats.priceRange}</div>
                            <div className="stat-label">Price Range</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="card-footer">
                      <div className="cta-button">
                        <span>Browse Category</span>
                        <div className="cta-arrow">
                          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M3 8h10m-5 5l5-5-5-5"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <h2>No categories found</h2>
              <p>Try adjusting your search term</p>
              <button 
                className="reset-btn"
                onClick={() => setSearchTerm('')}
              >
                Clear Search
              </button>
            </div>
          )}

          {/* Footer */}
          <footer className="categories-footer">
            <div className="footer-content">
              <div className="footer-text">
                <h3>Can't find what you're looking for?</h3>
                <p>Contact our team for personalized recommendations</p>
              </div>
              <button
                onClick={handleContactClick}
                className="footer-cta"
              >
                Contact Support
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </footer>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        className={`back-to-top ${isFilterSticky ? 'visible' : ''}`}
        onClick={() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }}
        aria-label="Back to top"
      >
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};

export default CategoriesPage;